import React, { useState, useEffect, useMemo } from 'react';
import './Users.css';
import { fetchUsers, updateUserRole, updateUserActivity } from '../api/userService';
import { getStoredUser } from '../utils/authStorage';
import UserTable from './UserTable';

const ACTION_ADMIN_EMAILS = ['r.sanjaiprasath@gmail.com', 'thirumal@vajraglobal.com'];
const PRIVILEGED_ROLES = ['admin', 'manager', 'technician'];

const Users = () => {
  const currentUser = getStoredUser();
  const currentRole = (currentUser?.role || 'viewer').toLowerCase();
  const currentEmail = (currentUser?.email || '').toLowerCase();
  const isPrivilegedRole = PRIVILEGED_ROLES.includes(currentRole);
  const isActionsAdmin = ACTION_ADMIN_EMAILS.includes(currentEmail);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    status: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const canEditRole = isPrivilegedRole || isActionsAdmin;
  const showActions = canEditRole || isActionsAdmin;

  useEffect(() => {
    loadUsers();
  }, [pagination.page, pagination.limit, filters]);

  // Send heartbeat to update current user's activity
  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        await updateUserActivity();
      } catch (err) {
        // Silently fail - heartbeat is not critical
        console.debug('Heartbeat failed:', err);
      }
    };

    // Send heartbeat immediately and then every 2 minutes
    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(heartbeatInterval);
  }, []);

  // Refresh users list periodically to show updated user status
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      // Only refresh if not currently loading to avoid conflicts
      if (!loading) {
        loadUsers();
      }
    }, 30 * 1000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount, loadUsers will use current state values

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Ensure status filter is exactly 'active', 'inactive', or 'all'
      const statusFilter = filters.status === 'active' || filters.status === 'inactive' ? filters.status : 'all';
      
      const response = await fetchUsers({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        role: filters.role,
        status: statusFilter,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
      setUsers(response.users || []);
      setPagination((prev) => ({
        ...prev,
        ...response.pagination,
      }));
      setError('');
    } catch (err) {
      console.error('Failed to load users', err);
      setError(err.response?.data?.message || 'Unable to load users. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSort = (column) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await updateUserRole(userId, role);
      await loadUsers();
    } catch (err) {
      console.error('Failed to update role', err);
      setError(err.response?.data?.message || 'Unable to update role.');
    }
  };

  const handleExport = async () => {
    try {
      // Fetch all users matching current filters (without pagination limit)
      const statusFilter = filters.status === 'active' || filters.status === 'inactive' ? filters.status : 'all';
      
      const response = await fetchUsers({
        page: 1,
        limit: 10000, // Large limit to get all users
        search: filters.search,
        role: filters.role,
        status: statusFilter,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });

      const usersToExport = response.users || [];
      
      if (usersToExport.length === 0) {
        setError('No users to export.');
        return;
      }

      // Role label mapping
      const roleLabelMap = {
        admin: 'Admin',
        manager: 'Manager',
        technician: 'Developer',
        user: 'Viewer',
      };

      // Prepare CSV data
      const header = [
        'Full Name',
        'Email',
        'Username',
        'Status',
        'Role',
        'Joined Date',
        'Last Active',
      ];

      const rows = usersToExport.map((user) => {
        const joinedDate = user.joinedDate ? new Date(user.joinedDate).toLocaleDateString() : '—';
        const lastActive = user.lastActive ? new Date(user.lastActive).toLocaleString() : 'Never';
        const role = roleLabelMap[user.role] || user.role || '—';
        const status = user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : '—';

        return [
          user.fullName || '—',
          user.email || '—',
          user.username || '—',
          status,
          role,
          joinedDate,
          lastActive,
        ];
      });

      // Create CSV content
      const csv =
        header.join(',') +
        '\n' +
        rows
          .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
          .join('\n');

      // Download CSV file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `users-export-${dateStr}.csv`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export users', err);
      setError(err.response?.data?.message || 'Failed to export users. Please try again.');
    }
  };

  const formattedUsers = useMemo(
    () =>
      users.map((user) => ({
        ...user,
        id: user._id || user.id,
        joinedDateLabel: user.joinedDate ? new Date(user.joinedDate).toLocaleDateString() : '—',
        lastActiveLabel: user.lastActive ? new Date(user.lastActive).toLocaleString() : 'Never',
      })),
    [users],
  );

  return (
    <div className="users-page">
      <div className="users-content-wrapper">
        <header className="users-header">
          <div>
            <h1>Viewer Management</h1>
            <p className="subtitle">
              Manage all viewers in one place. Control access, assign roles, and monitor activity across your platform.
            </p>
          </div>
        </header>

        <div className="users-toolbar">
          <div className="search-filters">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 14L11.1 11.1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                placeholder="Search"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            <div className="filter-dropdown">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 8C9.10457 8 10 7.10457 10 6C10 4.89543 9.10457 4 8 4C6.89543 4 6 4.89543 6 6C6 7.10457 6.89543 8 8 8Z" fill="currentColor" />
                <path d="M8 12C9.10457 12 10 11.1046 10 10C10 8.89543 9.10457 8 8 8C6.89543 8 6 8.89543 6 10C6 11.1046 6.89543 12 8 12Z" fill="currentColor" />
              </svg>
              <select value={filters.role} onChange={(e) => handleFilterChange('role', e.target.value)}>
                <option value="all">Role</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="technician">Developer</option>
              </select>
            </div>

            <div className="filter-dropdown">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="4" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
                <path d="M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <select 
                value={filters.status} 
                onChange={(e) => handleFilterChange('status', e.target.value)}
                style={{ 
                  color: filters.status !== 'all' ? '#2271b1' : '#2c3338',
                  fontWeight: filters.status !== 'all' ? '500' : '400'
                }}
              >
                <option value="all">Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="toolbar-actions">
            <button className="btn-export" type="button" onClick={handleExport} disabled={loading}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 2V11M8 11L5 8M8 11L11 8M3 13H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Export
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="users-table-wrapper">
          <div className="users-table-container">
            <UserTable
              users={formattedUsers}
              loading={loading}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSort={handleSort}
              canEditRole={canEditRole}
              onRoleChange={handleRoleChange}
              currentUserRole={currentRole}
              showActions={showActions}
            />
          </div>
        </div>

        <div className="pagination">
          <div className="pagination-info">
            <span>Rows per page:</span>
            <select
              value={pagination.limit}
              onChange={(e) =>
                setPagination((prev) => ({
                  ...prev,
                  limit: parseInt(e.target.value, 10),
                  page: 1,
                }))
              }
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span>of {pagination.total} rows</span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  page: Math.max(1, prev.page - 1),
                }))
              }
              disabled={pagination.page === 1}
            >
              ←
            </button>
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, idx) => {
              let pageNumber = idx + 1;
              if (pagination.totalPages > 5) {
                if (pagination.page <= 3) {
                  pageNumber = idx + 1;
                } else if (pagination.page >= pagination.totalPages - 2) {
                  pageNumber = pagination.totalPages - 4 + idx;
                } else {
                  pageNumber = pagination.page - 2 + idx;
                }
              }
              return (
                <button
                  key={pageNumber}
                  onClick={() => setPagination((prev) => ({ ...prev, page: pageNumber }))}
                  className={pagination.page === pageNumber ? 'active' : ''}
                >
                  {pageNumber}
                </button>
              );
            })}
            {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
              <>
                <span>...</span>
                <button onClick={() => setPagination((prev) => ({ ...prev, page: pagination.totalPages }))}>
                  {pagination.totalPages}
                </button>
              </>
            )}
            <button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  page: Math.min(prev.totalPages, prev.page + 1),
                }))
              }
              disabled={pagination.page === pagination.totalPages}
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;

